import apiClient from "@/shared/services/api/apiClient";

export type TeamMemberFeaturePermissionLevel = "ALLOWED" | "NONE";

export interface TeamMemberFeaturePermission {
  featureKey: string;
  permissionLevel: TeamMemberFeaturePermissionLevel;
}

export interface TeamMemberFeaturePermissionsResponse {
  teamMemberId: string;
  permissions: TeamMemberFeaturePermission[];
}

/** Fetch permissions for a Team Member managed by the current HR Admin. */
export const getTeamMemberFeaturePermissions = async (
  memberId: string
): Promise<TeamMemberFeaturePermissionsResponse> => {
  const response = await apiClient.get(
    `/team/${memberId}/feature-permissions`
  );

  const result = response.data?.data;

  if (!result || !Array.isArray(result.permissions)) {
    throw new Error("Invalid feature-permissions response from server.");
  }

  return result as TeamMemberFeaturePermissionsResponse;
};

/** Fetch the logged-in managed Team Member's own feature permissions. */
export const getMyFeaturePermissions = async (): Promise<
  TeamMemberFeaturePermission[]
> => {
  const response = await apiClient.get("/team/my-feature-permissions");
  const result = response.data?.data;
  const permissions = Array.isArray(result)
    ? result
    : result?.permissions;

  if (!Array.isArray(permissions)) {
    throw new Error("Invalid current-user feature-permissions response from server.");
  }

  return permissions as TeamMemberFeaturePermission[];
};

/** Immediately grant or revoke access to one registered module. */
export const setTeamMemberFeaturePermission = async (
  memberId: string,
  featureKey: string,
  permissionLevel: TeamMemberFeaturePermissionLevel
): Promise<{
  teamMemberId: string;
  featureKey: string;
  permissionLevel: TeamMemberFeaturePermissionLevel;
  changed: boolean;
}> => {
  const response = await apiClient.patch(
    `/team/${memberId}/feature-permissions`,
    { featureKey, permissionLevel }
  );

  const result = response.data?.data;

  if (!result || typeof result.permissionLevel !== "string") {
    throw new Error("Invalid feature-permission update response from server.");
  }

  return result;
};
